import { A11yModule } from '@angular/cdk/a11y';
import { ObserversModule } from '@angular/cdk/observers';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { AccordionComponent } from '@plugins/client/accordion/accordion/accordion.component';
import { AuthInterceptor } from '@plugins/client/auth';
import { SharedButtonComponent } from '@plugins/client/button/shared-button';
import { ColorsModule } from '@plugins/client/colors/colors.module';
import { AnimationDirective } from '@plugins/client/directives/animation.directive';
import { LetDirectiveModule } from '@plugins/client/directives/let-directive.module';
import { LineLimitDirective } from '@plugins/client/directives/line-limit.directive';
import { ListItemDirective } from '@plugins/client/directives/list/list-item.directive';
import { ListDirective } from '@plugins/client/directives/list/list.directive';
import { AutocompleteModule } from '@plugins/client/forms/autocomplete/autocomplete.module';
import { CheckboxComponent } from '@plugins/client/forms/checkbox/checkbox.component';
import { MultiSelectOptionDirective } from '@plugins/client/forms/multi-select/multi-select-option.directive';
import { MultiSelectComponent } from '@plugins/client/forms/multi-select/multi-select.component';
import { CancelIconComponent } from '@plugins/client/icons/cancel-icon';
import { SpinnerIconComponent } from '@plugins/client/icons/spinner-icon';
import { UserIconComponent } from '@plugins/client/icons/user-icon';
import { FocusOutsideDirective } from '@plugins/client/overlay/focus-outside.directive';
import { OverlayContainerComponent } from '@plugins/client/overlay/overlay-container/overlay-container.component';
import { AbbreviateNumberPipe } from '@plugins/client/pipes/math/abbreviate-number.pipe';
import { TooltipDirective } from '@plugins/client/tooltip/tooltip.directive';
import { TranslatePipe } from '@plugins/client/translate/pipes';
import { MiddlewareSdkModule } from '@plugins/middleware-sdk';

import { AvatarDisplayListComponent } from './avatar-display-list/avatar-display-list.component';
import { AvatarStackedListComponent } from './avatar-stacked-list/avatar-stacked-list.component';
import { AvatarStackedListZIndexPipe } from './avatar-stacked-list/avatar-stacked-list.pipe';
import {
  PickedEntitiesDirective,
  PrefetchEntitiesDirective,
} from './directives';
import { PickedEntitiesProviderComponent } from './picked-entities-provider/picked-entities-provider.component';
import { PickedUsersComponent } from './picked-users/picked-users.component';
import { UserDetailsPipe } from './pipes';
import { ProfileFieldPickerComponent } from './profile-fields-picker/profile-field-picker.component';
import { ProfileFieldPickerDropdownComponent } from './profile-fields-picker/profile-fields-picker-dropdown/profile-field-picker-dropdown.component';
import { ProfileDataService, UserDetailsService } from './services';
import { UserDetailsTranslationModule } from './translation-loader';
import { UserTextInitialsComponent } from './user-avatar/text-initials/text-initials.component';
import { UserAvatarComponent } from './user-avatar/user-avatar.component';
import { UserEntityComponent } from './user-entity/user.entity.component';
import { UserListComponent } from './user-list/user-list.component';
import { UserNameComponent } from './user-name/user-name.component';
import { UserProfileFieldPickerTogglerComponent } from './user-profile-field-picker-toggler/user-profile-field-picker-toggler.component';
import { UserProfileLinkDirective } from './user-profile-link/user-profile-link.directive';
import { UserSearchComponent } from './user-search/user-search.component';
import { UserSearchAndPickComponent } from './user-search-and-pick/user-search-and-pick.component';
export { USERDETAILS_DETAILED_TITLE } from './tokens';
export { UserDetailsService, ProfileDataService };

const nonExportedComponents = [UserTextInitialsComponent],
  components = [
    PickedEntitiesProviderComponent,
    PickedUsersComponent,
    UserAvatarComponent,
    UserNameComponent,
    UserEntityComponent,
    UserSearchComponent,
    UserSearchAndPickComponent,
    AvatarDisplayListComponent,
    AvatarStackedListComponent,
    UserListComponent,
    ProfileFieldPickerComponent,
    ProfileFieldPickerDropdownComponent,
    UserProfileFieldPickerTogglerComponent,
  ],
  pipes = [UserDetailsPipe],
  providedServices = [UserDetailsService, ProfileDataService],
  angularModules = [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MiddlewareSdkModule,
    ObserversModule,
    OverlayModule,
    A11yModule,
  ],
  sharedModules = [
    AutocompleteModule,
    AbbreviateNumberPipe,
    FocusOutsideDirective,
    OverlayContainerComponent,
    TranslatePipe,
    LetDirectiveModule,
    SharedButtonComponent,
    CancelIconComponent,
    UserIconComponent,
    ListDirective,
    ListItemDirective,
    ColorsModule,
    CheckboxComponent,
    MultiSelectComponent,
    MultiSelectOptionDirective,
    SpinnerIconComponent,
    AnimationDirective,
    TooltipDirective,
    AccordionComponent,
    LineLimitDirective,
  ],
  directives = [PickedEntitiesDirective, PrefetchEntitiesDirective];

export const imports = [
  [
    ...angularModules,
    ...sharedModules,
    UserDetailsTranslationModule,
    AvatarStackedListZIndexPipe,
    UserProfileLinkDirective,
  ],
];
export const declarations = [
  ...nonExportedComponents,
  ...components,
  ...pipes,
  ...directives,
];
export const providers = [
  ...providedServices,
  pipes,
  { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
];
export const exported = [
  ...components,
  ...pipes,
  ...directives,
  UserProfileLinkDirective,
];
@NgModule({
  imports,
  declarations,
  providers,
  exports: exported,
})
export class UserDetailsModule {}
